import { useEffect, useCallback } from 'react';
import { atom, useAtom } from 'jotai';
import { createId } from '@/lib/id';

/* ── Types ── */
export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
}

/* ── Atom ── */
export const toastsAtom = atom<Toast[]>([]);

/* ── Helpers ── */
const appendToast = (t: Toast) => (prev: Toast[]) => [...prev, t];
const removeToast = (id: string) => (prev: Toast[]) => prev.filter((t) => t.id !== id);

/* ── Hook ── */
export function useToast() {
  const [, setToasts] = useAtom(toastsAtom);

  const toast = useCallback(
    (message: string, variant: ToastVariant = 'info') => {
      const id = createId();
      setToasts(appendToast({ id, message, variant }));
      setTimeout(() => setToasts(removeToast(id)), 4000);
    },
    [setToasts]
  );

  return { toast };
}

/* ── Variant config ── */
const VARIANT_CONFIG: Record<ToastVariant, { accent: string; icon: React.ReactNode }> = {
  success: {
    accent: '#2e7d32',
    icon: (
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
      </svg>
    ),
  },
  error: {
    accent: '#e53935',
    icon: (
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
      </svg>
    ),
  },
  warning: {
    accent: '#f57c00',
    icon: (
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
    ),
  },
  info: {
    accent: '#1565c0',
    icon: (
      <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
      </svg>
    ),
  },
};

/* ── Single Toast ── */
interface ToastItemProps {
  readonly toast: Toast;
  readonly onDismiss: () => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const { accent, icon } = VARIANT_CONFIG[toast.variant];

  useEffect(() => {
    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div
      role="alert"
      className="flex items-center gap-3 pl-4 pr-3 py-3 rounded-xl shadow-lg text-sm font-medium text-white relative overflow-hidden"
      style={{
        minWidth: '280px',
        maxWidth: '360px',
        background: '#1f2937',
        fontFamily: "var(--dh-font, 'Inter', sans-serif)",
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
      }}
    >
      <div
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl"
        style={{ background: accent }}
      />
      <span style={{ color: accent }}>{icon}</span>
      <p className="flex-1 text-white/90 font-normal leading-snug" style={{ fontSize: '0.8125rem' }}>
        {toast.message}
      </p>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="h-6 w-6 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition shrink-0"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/* ── Container ── */
export function ToastContainer() {
  const [toasts, setToasts] = useAtom(toastsAtom);
  const dismiss = (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id));

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[1400] flex flex-col gap-2 items-center">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}
